// ⑤ 開始画面設計のゲーム適用コア（本家 2026-07-18「ゲーム開始画面の改良」のツール反映）。
// - scaffold（②）: sevenfold 由来アートの無害化＋素材なしフォールバック（start-menu--no-art）へ初期化
// - 開始画面設計工程（⑤）: 生成/アップロード済み素材と調整値を index.html / 画像 / start-screen.json へ適用
// 適用状態の正本は maps/_shared/start-screen.json（ブラウザ版 preview-sw.js はこれを読んでシェルへ注入する）。
// HTML 書換はすべて冪等（再適用で同じ結果）。エンジン構造が変わって書換対象を見失った場合は
// 黙って通さず Error にする（scaffold.mjs rewriteTextFile と同じ方針）。
import { existsSync, readFileSync, writeFileSync, readdirSync, unlinkSync } from 'node:fs';
import path from 'node:path';
import { writeJson } from './json-utils.mjs';

export const START_SCREEN_GAME_DATA_REL = 'maps/_shared/start-screen.json';
const ART_CSS_BEGIN = '/* START-SCREEN-ART:BEGIN（ツール生成・再適用で全置換。手動編集しない） */';
const ART_CSS_END = '/* START-SCREEN-ART:END */';

const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
const cssNum = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

/** キャラ選択ボタン群の内側 HTML（②スキャフォールドと preview-sw.js の注入で同一マークアップにする）。
 *  顔サムネイル（.character-avatar）は常に出力し、素材なしフォールバック時は CSS（start-menu--no-art）で隠す。
 *  data-character-art はツールが素材適用時に URL を書き込む（本家仕様 §8。ランタイムは CSS から解決）。 */
export function buildCharacterButtonsHtml(chars, { indent = '                    ' } = {}) {
    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return chars.map((c, i) => [
        `${indent}<button class="character-button compact-text${i === 0 ? ' active' : ''}" id="select-${escHtml(c.id)}" data-character-art="${escHtml(c.artFile ?? '')}" aria-pressed="${i === 0 ? 'true' : 'false'}" data-i18n-title="startMenu.select${escHtml(capitalize(c.id))}Title"${c.titleJa ? ` title="${escHtml(c.titleJa)}"` : ''}>`,
        `${indent}    <span class="character-avatar" aria-hidden="true"></span>`,
        `${indent}    <span class="character-copy">`,
        `${indent}        <span class="character-name" data-i18n="character.${escHtml(c.id)}.name">${escHtml(c.nameJa ?? c.id)}</span>`,
        `${indent}        <span class="character-desc" data-i18n="character.${escHtml(c.id)}.description">${escHtml(c.descJa ?? '')}</span>`,
        `${indent}    </span>`,
        `${indent}</button>`,
    ].join('\n')).join('\n');
}

/** index.html の .character-buttons 内側を差し替える（scaffold の従来ロジックを新マークアップで共通化）。 */
export function replaceCharacterButtonsHtml(html, chars, opts = {}) {
    const marker = '<div class="character-buttons">';
    const start = html.indexOf(marker);
    if (start < 0) throw new Error('index.html: <div class="character-buttons"> が見つかりません（sevenfold 側の実装変更の可能性。手動対応してください）');
    const innerStart = start + marker.length;
    const end = html.indexOf('</div>', innerStart);
    if (end < 0) throw new Error('index.html: character-buttons の閉じタグが見つかりません');
    return html.slice(0, innerStart) + '\n' + buildCharacterButtonsHtml(chars, opts) + '\n                ' + html.slice(end);
}

/** sevenfold 実装由来の開始画面アート参照（背景 URL・顔サムネ CSS・立ち絵 img）を無害化する。
 *  ②スキャフォールド直後のゲームは素材未生成のため、壊れた画像参照を 1 つも残さない。 */
export function neutralizeEngineStartScreenArt(html) {
    const counts = {};
    // 1) 背景画像 URL → none（マーカー CSS ブロックが後から上書きする）
    const bgRe = /background-image: url\('start-menu-background\.[a-z]+'\);/;
    if (!bgRe.test(html)) throw new Error('index.html: .start-menu-background の background-image が見つかりません（sevenfold 側の実装変更の可能性。手動対応してください）');
    html = html.replace(bgRe, 'background-image: none;');
    counts.background = 1;
    // 2) 顔サムネイルのアート CSS 行（#select-kai .character-avatar { ... } 等）を削除
    const avatarRe = /^[ \t]*#select-[a-z0-9_]+ \.character-avatar \{ background-image: url\('start-menu-[^']+'\); \}\r?\n/gm;
    const avatarMatches = html.match(avatarRe) ?? [];
    if (avatarMatches.length === 0) throw new Error('index.html: #select-* .character-avatar のアート CSS が見つかりません（sevenfold 側の実装変更の可能性。手動対応してください）');
    html = html.replace(avatarRe, '');
    counts.avatarCss = avatarMatches.length;
    // 3) 立ち絵 <img> を除去（素材適用時に applyStartScreenToGameDir が再挿入する）
    html = patchSelectedCharacterArtImg(html, null);
    counts.stageImg = 1;
    // 4) 素材なしフォールバックのクラスを付与
    html = patchStartMenuArtClass(html, false);
    return { html, counts };
}

/** #start-menu の start-menu--no-art クラスを artMode に応じて付け外しする（冪等）。 */
export function patchStartMenuArtClass(html, artMode) {
    const re = /<div id="start-menu"(?: class="([^"]*)")?>/;
    const m = html.match(re);
    if (!m) throw new Error('index.html: <div id="start-menu"> が見つかりません（sevenfold 側の実装変更の可能性。手動対応してください）');
    const classes = (m[1] ?? '').split(/\s+/).filter((c) => c && c !== 'start-menu--no-art');
    if (!artMode) classes.push('start-menu--no-art');
    const attr = classes.length > 0 ? ` class="${classes.join(' ')}"` : '';
    return html.replace(re, `<div id="start-menu"${attr}>`);
}

/** 立ち絵フレーム内の <img id="selected-character-art"> を設定（art=null で除去）。冪等。 */
export function patchSelectedCharacterArtImg(html, art) {
    const re = /(<div class="selected-character-art-frame">)([\s\S]*?)(\r?\n[ \t]*<\/div>)/;
    if (!re.test(html)) throw new Error('index.html: .selected-character-art-frame が見つかりません（sevenfold 側の実装変更の可能性。手動対応してください）');
    const inner = art
        ? [
            '',
            '                    <img',
            '                        id="selected-character-art"',
            '                        class="selected-character-art"',
            `                        src="${escHtml(art.file)}"`,
            `                        data-character="${escHtml(art.characterId)}"`,
            '                        alt=""',
            '                        decoding="async"',
            '                    >',
        ].join('\n')
        : '';
    return html.replace(re, (_, open, _inner, close) => `${open}${inner}${close}`);
}

/** 各キャラ選択ボタンの data-character-art 属性値を書き換える（artByCharId に無い ID は空にする）。 */
export function patchCharacterArtAttrs(html, artByCharId) {
    return html.replace(/(<button[^>]*id="select-([a-z0-9_]+)"[^>]*data-character-art=")[^"]*(")/g,
        (_, before, id, after) => `${before}${escHtml(artByCharId?.[id] ?? '')}${after}`);
}

/** 適用状態からマーカー内の CSS を生成する。値はすべてツールのプロジェクトデータ由来
 *  （本家の固定 CSS 値を全作品へ流用しない・変更点まとめ §14.3）。 */
export function buildStartScreenArtCss(state) {
    if (!state?.artMode) return '';
    const lines = [];
    if (state.background?.image) {
        // 横方向は中央固定（横クロップは廃止・キャラ基準位置で調整）。背景スケールはエンジン既定の
        // transform: scale(1.025) を基準に乗算した値で上書きする
        const bgScale = Math.round(1.025 * cssNum(state.background.scale, 1) * 1000) / 1000;
        lines.push(
            '.start-menu-background {',
            `    background-image: url('${state.background.image}');`,
            `    background-position: 50% ${cssNum(state.background.positionY, 50)}%;`,
            `    transform: scale(${bgScale});`,
            '}',
        );
    }
    // 配置プレビュー（16:9 基準）と実ゲームの見え方を一致させるため、立ち絵の縮尺・位置を
    // 「画面幅基準」の式へ上書きする（2026-07-19）。エンジン既定は高さ基準（74vh・px 固定 translate）で、
    // 横長ウィンドウでは幅基準の背景 cover とずれるため。16:9 画面では従来のエンジン既定と同値になる:
    //   41.63vw = 74vh×(9/16) ／ 縦基準 calc(52px - 6.42vw) = 1440×810 時の footer+padding 52px + translateY -40.5px
    // 横は本家 §9.1 の clamp を保持し、台座中心（stageCenterX）との差分 frameShiftVw を加算する。
    // 縦の持ち上げはエンジン既定と同じ [-70px, -36px] に制限する。
    // 高さの歯止めは「画面（ウィンドウ）からはみ出す分」だけにする（FIT）。かつて立ち絵行の高さ
    // （calc(100% + TY)）で抑えていたが、キャラ個別の縦位置（margin-bottom）が加算されると行から
    // はみ出し、行の overflow: hidden で頭が削れた（2026-07-26 実障害）。行では抑えず前面へ出す。
    // 16:9 では 41.63vw = 74vh < 92vh のため FIT は効かず、配置プレビューと同じ大きさになる。
    const TY = 'clamp(-70px, calc(52px - 6.42vw), -36px)';
    const FIT = '92vh';
    lines.push(
        // 立ち絵は常に最前面。タイトル・操作パネルへかぶってもよいので、行の切り取りを解除して前へ出す
        //（②スキャフォールド済みの既存ゲームは engine 側 CSS が古いため、ここで必ず上書きする）
        '.start-menu-main-layout {',
        '    position: relative;',
        '    z-index: 2;',
        '}',
        '.selected-character-stage {',
        '    z-index: 2;',
        '    overflow: visible;',
        '}',
        '.selected-character-art-frame {',
        `    transform: translate(calc(clamp(-230px, calc(305px - 28vw), 46px) + ${cssNum(state.frameShiftVw)}vw), ${TY});`,
        '}',
        '.selected-character-art {',
        `    max-height: min(41.63vw, 800px, ${FIT});`,
        '}',
    );
    const vwOf = (px) => Math.round((cssNum(px) / 14.4) * 1000) / 1000; // 1440px 幅基準の px → vw
    for (const c of state.characters ?? []) {
        if (!c?.image) continue;
        lines.push(`#select-${c.id} .character-avatar { background-image: url('${c.image}'); }`);
        const perChar = [];
        if (cssNum(c.offsetX) !== 0) perChar.push(`    margin-left: ${vwOf(c.offsetX)}vw;`);
        if (cssNum(c.offsetY) !== 0) perChar.push(`    margin-bottom: ${vwOf(c.offsetY)}vw;`);
        if (cssNum(c.scale, 1) !== 1) perChar.push(`    max-height: min(calc(min(41.63vw, 800px) * ${cssNum(c.scale, 1)}), ${FIT});`);
        if (c.maskEnabled === false) perChar.push('    -webkit-mask-image: none;', '    mask-image: none;');
        if (perChar.length > 0) {
            lines.push(`#selected-character-art[data-character="${c.id}"] {`, ...perChar, '}');
        }
    }
    return lines.join('\n');
}

/** マーカー付き CSS ブロックを設定（既存ブロックは全置換・無ければ </style> 直前へ挿入）。冪等。 */
export function patchStartScreenArtCss(html, cssText) {
    const indent = '        ';
    const body = cssText
        ? cssText.split('\n').map((line) => (line === '' ? '' : indent + line)).join('\n') + '\n'
        : '';
    const block = `${indent}${ART_CSS_BEGIN}\n${body}${indent}${ART_CSS_END}`;
    const beginAt = html.indexOf(ART_CSS_BEGIN);
    if (beginAt >= 0) {
        const endAt = html.indexOf(ART_CSS_END, beginAt);
        if (endAt < 0) throw new Error('index.html: START-SCREEN-ART の終了マーカーが見つかりません（手動編集で壊れています）');
        const lineStart = html.lastIndexOf('\n', beginAt) + 1;
        return html.slice(0, lineStart) + block + html.slice(endAt + ART_CSS_END.length);
    }
    const styleClose = html.match(/[ \t]*<\/style>/);
    if (!styleClose) throw new Error('index.html: </style> が見つかりません（sevenfold 側の実装変更の可能性。手動対応してください）');
    return html.replace(styleClose[0], `${block}\n${styleClose[0]}`);
}

/** ゲームディレクトリ直下の開始画面アート画像（start-menu-*.png 等）を列挙する。 */
export function listGameStartArtFiles(gameDir, extensions = ['png', 'jpg', 'jpeg', 'webp']) {
    if (!existsSync(gameDir)) return [];
    const re = new RegExp(`^start-menu-[a-z0-9_.-]+\\.(${extensions.join('|')})$`, 'i');
    return readdirSync(gameDir).filter((name) => re.test(name));
}

/**
 * 開始画面素材一式をゲームへ適用する（冪等）。
 * @param {string} gameDir ゲームディレクトリ（Node 版=実ファイル / ブラウザ版=VFS。index.html はあれば書換）
 * @param {object} state {
 *   artMode: boolean,                        // 背景＋全プレイアブル立ち絵がそろった時のみ true（壊れた参照を出さない）
 *   panelSide: 'right'|'left',
 *   background: { image, positionX, positionY } | null,
 *   characters: [{ id, image, offsetX, offsetY, scale, maskEnabled }],
 * }
 * @param {object} images { [fileName]: Uint8Array|Buffer }  // 適用する画像バイナリ（artMode=false なら空でよい）
 */
export function applyStartScreenToGameDir(gameDir, state, images = {}) {
    const applied = { artMode: !!state?.artMode, writtenImages: [], removedImages: [], htmlPatched: false };
    // 1) 画像の同期（適用対象を書き、対象外の start-menu-* を削除して参照切れ・残骸を防ぐ）
    const wanted = new Set();
    if (state?.artMode) {
        if (state.background?.image) wanted.add(state.background.image);
        for (const c of state.characters ?? []) if (c?.image) wanted.add(c.image);
    }
    for (const [name, bytes] of Object.entries(images)) {
        if (!wanted.has(name)) continue;
        writeFileSync(path.join(gameDir, name), bytes);
        applied.writtenImages.push(name);
    }
    for (const name of listGameStartArtFiles(gameDir)) {
        if (!wanted.has(name)) {
            unlinkSync(path.join(gameDir, name));
            applied.removedImages.push(name);
        }
    }
    // 2) 適用状態の正本（preview-sw.js のシェル注入・再適用・検査が読む）
    const gameData = {
        formatVersion: '1.0',
        artMode: !!state?.artMode,
        panelSide: state?.panelSide === 'left' ? 'left' : 'right',
        stageCenterX: cssNum(state?.stageCenterX, 25),
        frameShiftVw: cssNum(state?.frameShiftVw, 0),
        background: state?.artMode && state?.background?.image ? {
            image: state.background.image,
            scale: cssNum(state.background.scale, 1),
            positionY: cssNum(state.background.positionY, 50),
        } : null,
        characters: state?.artMode
            ? (state.characters ?? []).filter((c) => c?.image).map((c) => ({
                id: c.id,
                image: c.image,
                offsetX: cssNum(c.offsetX),
                offsetY: cssNum(c.offsetY),
                scale: cssNum(c.scale, 1),
                maskEnabled: c.maskEnabled !== false,
            }))
            : [],
    };
    writeJson(path.join(gameDir, START_SCREEN_GAME_DATA_REL), gameData);
    // 3) index.html の書換（Node 版の実ゲームのみ。ブラウザ版プレビューはシェルを preview-sw が合成）
    const htmlPath = path.join(gameDir, 'index.html');
    if (existsSync(htmlPath)) {
        let html = readFileSync(htmlPath, 'utf-8');
        html = patchStartMenuArtClass(html, !!state?.artMode);
        html = patchStartScreenArtCss(html, buildStartScreenArtCss(gameData));
        const firstChar = gameData.characters[0] ?? null;
        html = patchSelectedCharacterArtImg(html, gameData.artMode && firstChar
            ? { file: firstChar.image, characterId: firstChar.id }
            : null);
        html = patchCharacterArtAttrs(html, Object.fromEntries(gameData.characters.map((c) => [c.id, c.image])));
        writeFileSync(htmlPath, html, 'utf-8');
        applied.htmlPatched = true;
    }
    return applied;
}
